import * as React from "react";
import { StatusAdornment } from "../display/StatusAdornment";

import { useFormFieldProps } from "../salt/form-field-context";
import { useControlled } from "./useControlled";
import "./multiline-input.css";

/**
 * MultilineInput, replacing `@salt-ds/core`'s. It is Input's sibling: the same wrapper-draws-
 * the-frame structure, the same FormField merge, the same aria concatenation — over a
 * `<textarea>` instead of an `<input>`.
 *
 * ── THE AUTO-GROW IS THE ONLY GENUINELY NEW BEHAVIOUR, AND IT HAS A TWIST ─────────────
 *
 * The box grows to fit its content: set the height to `auto`, read `scrollHeight`, write it
 * back. Two details make it correct rather than merely working.
 *
 * `overflow` is forced to `hidden` for the measurement and restored afterwards. Without that,
 * a textarea already showing a scrollbar measures its own scrollbar into `scrollHeight` and
 * the box gains a few pixels on every keystroke — a slow, one-directional creep.
 *
 * And it STOPS once the user has dragged the resize handle. `previousHeight` records what this
 * component last wrote; if the element's height no longer matches, a human moved it, and
 * auto-grow stands down permanently. Otherwise the next keystroke snaps the box back and the
 * resize handle appears broken.
 *
 * It runs in a LAYOUT effect so the height is correct before paint — an ordinary effect resizes
 * after the browser has already shown the wrong height, which reads as a flicker.
 */
export type MultilineInputVariant = "primary" | "secondary";
export type MultilineInputValidationStatus = "error" | "warning" | "success";

type DataAttributes = Record<`data-${string}`, unknown>;

export interface MultilineInputProps
  extends Omit<React.ComponentPropsWithoutRef<"div">, "defaultValue">,
    Pick<
      React.ComponentPropsWithoutRef<"textarea">,
      "disabled" | "value" | "defaultValue" | "placeholder" | "rows"
    > {
  bordered?: boolean;
  endAdornment?: React.ReactNode;
  readOnly?: boolean;
  startAdornment?: React.ReactNode;
  textAreaProps?: Partial<React.TextareaHTMLAttributes<HTMLTextAreaElement>> & DataAttributes;
  textAreaRef?: React.Ref<HTMLTextAreaElement>;
  validationStatus?: MultilineInputValidationStatus;
  variant?: MultilineInputVariant;
}

function join(...values: (string | undefined)[]): string | undefined {
  return values.filter(Boolean).join(" ") || undefined;
}

export const MultilineInput = React.forwardRef<HTMLDivElement, MultilineInputProps>(
  function MultilineInput(
    {
      bordered = false,
      className,
      disabled,
      endAdornment,
      id,
      placeholder,
      readOnly,
      role,
      rows = 3,
      startAdornment,
      style,
      textAreaProps = {},
      textAreaRef,
      validationStatus: validationStatusProp,
      value: valueProp,
      defaultValue: defaultValueProp = valueProp === undefined ? "" : undefined,
      variant = "primary",
      ...other
    },
    ref,
  ) {
    const [inputElement, setInputElement] = React.useState<HTMLTextAreaElement | null>(null);
    const setTextAreaRefs = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        setInputElement(node);
        if (typeof textAreaRef === "function") textAreaRef(node);
        else if (textAreaRef)
          (textAreaRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
      },
      [textAreaRef],
    );

    const [focused, setFocused] = React.useState(false);

    const {
      "aria-describedby": textAreaDescribedBy,
      "aria-labelledby": textAreaLabelledBy,
      className: textAreaClassName,
      onBlur,
      onChange,
      onFocus,
      required: textAreaRequired,
      ...restTextAreaProps
    } = textAreaProps;

    const {
      a11yProps: {
        "aria-describedby": formFieldDescribedBy,
        "aria-labelledby": formFieldLabelledBy,
      } = {},
      disabled: formFieldDisabled,
      readOnly: formFieldReadOnly,
      necessity: formFieldRequired,
      validationStatus: formFieldValidationStatus,
    } = useFormFieldProps();

    const isDisabled = disabled || formFieldDisabled;
    const isReadOnly = readOnly || formFieldReadOnly;
    const validationStatus = formFieldValidationStatus ?? validationStatusProp;
    const isRequired = formFieldRequired
      ? ["required", "asterisk"].includes(formFieldRequired)
      : textAreaRequired;

    const [value, setValue] = useControlled<
      React.TextareaHTMLAttributes<HTMLTextAreaElement>["value"]
    >({ controlled: valueProp, default: defaultValueProp ?? "" });

    const previousHeight = React.useRef<string | undefined>(undefined);
    const changeHeight = React.useCallback(() => {
      const input = inputElement;
      if (!input) return;
      // A height this component did not write means the user dragged the resize handle.
      const hasBeenManuallyResized =
        previousHeight.current !== undefined && input.style.height !== previousHeight.current;
      if (hasBeenManuallyResized) return;

      const previousOverflow = input.style.overflow;
      // Hidden during the measurement: a textarea already showing a scrollbar measures the
      // scrollbar into scrollHeight and creeps a few pixels taller on every keystroke.
      input.style.overflow = "hidden";
      input.style.height = "auto";
      const newHeight = `${input.scrollHeight + (input.offsetHeight - input.clientHeight)}px`;
      input.style.height = newHeight;
      previousHeight.current = newHeight;
      input.style.overflow = previousOverflow;
    }, [inputElement]);

    React.useLayoutEffect(() => {
      changeHeight();
    }, [value, changeHeight]);

    const classes = ["saltMultilineInput", `saltMultilineInput-${variant}`];
    if (endAdornment) classes.push("saltMultilineInput-withAdornmentRow");
    if (bordered) classes.push("saltMultilineInput-bordered");
    if (!isDisabled && focused) classes.push("saltMultilineInput-focused");
    if (isDisabled) classes.push("saltMultilineInput-disabled");
    if (isReadOnly) classes.push("saltMultilineInput-readOnly");
    if (validationStatus) classes.push(`saltMultilineInput-${validationStatus}`);
    if (className) classes.push(className);

    return (
      <div
        className={classes.join(" ")}
        ref={ref}
        style={{ "--saltMultilineInput-rows": rows, ...style } as React.CSSProperties}
        {...other}
      >
        {startAdornment ? (
          <div className="saltMultilineInput-startAdornmentContainer">{startAdornment}</div>
        ) : null}
        <div className="saltMultilineInput-wrapper">
          <textarea
            aria-describedby={join(formFieldDescribedBy, textAreaDescribedBy)}
            aria-labelledby={join(formFieldLabelledBy, textAreaLabelledBy)}
            className={
              textAreaClassName
                ? `saltMultilineInput-textarea ${textAreaClassName}`
                : "saltMultilineInput-textarea"
            }
            disabled={isDisabled}
            id={id}
            onBlur={(event) => {
              onBlur?.(event);
              setFocused(false);
            }}
            onChange={(event) => {
              setValue(event.target.value);
              onChange?.(event);
              changeHeight();
            }}
            onFocus={
              !isDisabled
                ? (event) => {
                    onFocus?.(event);
                    setFocused(true);
                  }
                : undefined
            }
            placeholder={placeholder}
            readOnly={isReadOnly}
            ref={setTextAreaRefs}
            required={isRequired}
            role={role}
            rows={rows}
            tabIndex={isDisabled ? -1 : 0}
            value={value}
            {...restTextAreaProps}
          />
        </div>
        <div className="saltMultilineInput-suffixAdornments">
          {!isDisabled && validationStatus ? (
            <div className="saltMultilineInput-statusAdornmentContainer">
              <StatusAdornment status={validationStatus} />
            </div>
          ) : null}
          {endAdornment ? (
            <div className="saltMultilineInput-endAdornmentContainer">{endAdornment}</div>
          ) : null}
        </div>
        <div className="saltMultilineInput-activationIndicator" />
      </div>
    );
  },
);
